<!-- eslint-disable vue/html-self-closing -->
<template>
  <section class="gf-package-card-static">
    <gf-entity-card
      :description="description"
      :is-dark-header="true"
    >
      <template #headerTitle>
        <img
          class="avatar-squared"
          :src="imageSrc"
        />
        <div class="gf-package-card-static__header">
          <div class="title">
            <h6 class="mb-0">
              <a :href="packageUrl">
                {{ name }}
              </a>
            </h6>
          </div>
        </div>
      </template>
      <template #footerContent>
        <div class="gf-package-card-static__footer-content">
          <div class="gf-package-card-static__footer-info">
            <small class="text-muted">{{ $t('package.version') }} {{ version }}</small>
            <span class="gf-dot-xs"></span>
            <small class="text-muted">{{ $t('package.createdAt') }} {{ createdAt }}</small>
            <span
              v-if="
                (isOwnerLabel && ownerAliasType !== 'global') ||
                (isCompanyAgregation && ownerAliasType === 'project')
              "
              :class="ownerBadgeClass"
            >
              <i
                class="gf-icon mr-1"
                :class="ownerIconClass"
              ></i>
              <a
                class="ml-1"
                :href="ownerLink"
              >
                <span class="mr-1">{{ ownerIconText }}</span>
                <span class="owner-alias">{{ ownerAlias }}</span>
              </a>
            </span>
            <span
              v-for="(category, index) in categoriesList?.length >= 3
                ? categoriesList.slice(0, 3)
                : categoriesList"
              :key="index"
              class="gf-topic-badge"
            >
              <a
                :href="
                  packageType === 'container'
                    ? `/search/container?categories=${category}`
                    : `/search/package?categories=${category}`
                "
              >
                {{ category }}
              </a>
            </span>
            <span
              v-if="categoriesList?.length > 3"
              class="gf-topic-badge"
            >
              <a :href="packageUrl">+{{ categoriesList?.length - 3 }}</a>
            </span>
          </div>
          <div class="gf-package-card-static__footer-downloads">
            <i class="gf-icon gf-icon-download gf-fs-2 mr-1"></i>
            <span class="counter">{{ formatOutputNumber(parseInt(downloadCount)) }}</span>
          </div>
        </div>
      </template>
    </gf-entity-card>
  </section>
</template>

<script>
import GfEntityCard from '@shared/shared-components/shared/GfEntityCard';
import HelperS from '@shared/services/helper.service';
import RegistryMessages from '@/shared-modules/modules/registry/registry.messages';

export default {
  name: 'GfPackageCardStatic',
  components: {
    GfEntityCard
  },
  props: {
    description: {
      type: String,
      default: null
    },
    name: {
      type: String,
      default: null
    },
    showOwnerLabel: {
      type: String,
      default: null
    },
    ownerAlias: {
      type: String,
      default: null
    },
    ownerAliasType: {
      type: String,
      default: null
    },
    imageSrc: {
      type: String,
      default: null
    },
    packageUrl: {
      type: String,
      default: null
    },
    version: {
      type: String,
      default: null
    },
    createdAt: {
      type: String,
      default: null
    },
    ownerLink: {
      type: String,
      default: null
    },
    downloadCount: {
      type: String,
      default: '0'
    },
    showCompanyAgregation: {
      type: String,
      default: 'false'
    },
    categories: {
      type: String,
      default: ''
    },
    packageType: {
      type: String,
      default: 'generic'
    }
  },
  computed: {
    isUserAuthtorized() {
      return this.$store.getters.getIsUserAuthtorized;
    },
    ownerIconClass() {
      let resultClass = '';
      switch (this.ownerAliasType) {
        case 'project':
          resultClass = 'gf-icon-folder';
          break;
        case 'team':
          resultClass = 'gf-icon-teams';
          break;
        case 'company':
          resultClass = 'gf-icon-companies';
          break;
        default:
          break;
      }
      return resultClass;
    },
    ownerIconText() {
      switch (this.ownerAliasType) {
        case 'project':
          return this.$t('package.owner.inProject');
        case 'team':
          return this.$t('package.owner.inTeam');
        case 'company':
          return this.$t('package.owner.inCompany');
        default:
          return '';
      }
    },
    ownerBadgeClass() {
      let resultClass = '';
      switch (this.ownerAliasType) {
        case 'project':
          resultClass = 'project-badge';
          break;
        case 'team':
          resultClass = 'gf-team-badge';
          break;
        case 'company':
          resultClass = 'gf-company-badge';
          break;
        default:
          break;
      }
      return resultClass;
    },
    isOwnerLabel() {
      return this.showOwnerLabel === 'true';
    },
    isCompanyAgregation() {
      return this.showCompanyAgregation === 'true';
    },
    categoriesList() {
      return this.categories?.length > 0 ? this.categories.split(', ') : [];
    }
  },
  methods: {
    formatOutputNumber(number) {
      return HelperS.formatOutputNumber(number, 0);
    }
  },
  i18n: {
    messages: RegistryMessages
  }
};
</script>

<style lang="scss">
$this: '.gf-package-card-static';

#{$this} {
  height: 100%;

  #{$this}__header {
    width: calc(100% - 32px);
    display: flex;
    flex-direction: column;
    gap: 4px;

    & .info {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  #{$this}__footer-content {
    display: flex;
    flex-direction: column;
    gap: 8px;

    @include theme.sm {
      flex-direction: row;
    }

    #{$this}__footer-info {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;

      & > span {
        flex-wrap: wrap;

        & .owner-alias {
          max-width: 120px;
          text-wrap: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }

    #{$this}__footer-downloads {
      margin: auto 0 0 auto;
      font-size: 14px;

      & > i {
        color: var(--text-muted);
      }
    }
  }
}
</style>
